import { Field } from '@catering/ui';
import type { JSX } from 'react';

export interface CalendarFiltersServiceType {
  id: string;
  name: string;
}

export interface CalendarFiltersPosition {
  id: string;
  name: string;
  serviceTypeId: string;
}

export interface CalendarFiltersProps {
  from: string;
  to: string;
  serviceTypeId: string | undefined;
  positionId: string | undefined;
  isPublished: boolean | undefined;
  serviceTypes: CalendarFiltersServiceType[];
  positions: CalendarFiltersPosition[];
  onFromChange: (from: string) => void;
  onToChange: (to: string) => void;
  onServiceTypeChange: (serviceTypeId: string | undefined) => void;
  onPositionChange: (positionId: string | undefined) => void;
  onPublishedChange: (isPublished: boolean | undefined) => void;
}

/**
 * Date range, service type, position and publication state, kept in the URL
 * by `calendar-list-page.tsx` (same `validateSearch` convention as
 * `contractListSearchSchema`). `serviceTypeId` filters client-side over the
 * already-fetched range: `GET /calendar` (`apps/api`) only accepts
 * `positionId`, not `serviceTypeId` (`menu-calendar/design.md` §5) —
 * filtering by the broader, more useful grouping the grid already displays
 * as column groups happens here instead, same client-side-filter criterion
 * `ContractListPage` already applies over its own unfiltered API response.
 * `positionId` reaches `GET /calendar` directly, and its own options narrow
 * to the selected `serviceTypeId` so the dropdown never offers a position
 * the service type filter already excludes.
 */
export function CalendarFilters({
  from,
  to,
  serviceTypeId,
  positionId,
  isPublished,
  serviceTypes,
  positions,
  onFromChange,
  onToChange,
  onServiceTypeChange,
  onPositionChange,
  onPublishedChange,
}: CalendarFiltersProps): JSX.Element {
  const visiblePositions = positions.filter(
    (position) => serviceTypeId === undefined || position.serviceTypeId === serviceTypeId,
  );
  return (
    <div className="flex flex-wrap items-end gap-4">
      <Field
        label="Desde"
        name="from"
        type="date"
        value={from}
        onChange={(event) => onFromChange(event.target.value)}
      />

      <Field
        label="Hasta"
        name="to"
        type="date"
        value={to}
        onChange={(event) => onToChange(event.target.value)}
      />

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="calendar-service-type-filter"
          className="text-sm font-medium text-slate-900"
        >
          Tipo de servicio
        </label>
        <select
          id="calendar-service-type-filter"
          value={serviceTypeId ?? ''}
          onChange={(event) =>
            onServiceTypeChange(event.target.value === '' ? undefined : event.target.value)
          }
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Todos los tipos</option>
          {serviceTypes.map((serviceType) => (
            <option key={serviceType.id} value={serviceType.id}>
              {serviceType.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="calendar-position-filter" className="text-sm font-medium text-slate-900">
          Posición
        </label>
        <select
          id="calendar-position-filter"
          value={positionId ?? ''}
          onChange={(event) =>
            onPositionChange(event.target.value === '' ? undefined : event.target.value)
          }
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Todas las posiciones</option>
          {visiblePositions.map((position) => (
            <option key={position.id} value={position.id}>
              {position.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="calendar-published-filter" className="text-sm font-medium text-slate-900">
          Publicación
        </label>
        <select
          id="calendar-published-filter"
          value={isPublished === undefined ? '' : String(isPublished)}
          onChange={(event) =>
            onPublishedChange(event.target.value === '' ? undefined : event.target.value === 'true')
          }
          className="h-11 rounded-md border border-slate-300 px-3 text-sm"
        >
          <option value="">Todas</option>
          <option value="true">Publicadas</option>
          <option value="false">Borradores</option>
        </select>
      </div>
    </div>
  );
}
